// Remote-player rendering, 2D top-down (docs/multiplayer-sync/roadmap.md
// Phase 4: "2D: reuse the existing sprite character renderer"). Visually
// mirrors Tilemap2DScene.tsx's own `Player` component exactly (a flipbook-
// animated quad at `TILE_SIZE * 0.8`, using the map's own `playerSprite` -
// the same "generic avatar" reasoning `RemotePlayers.tsx` (3D) uses) - but
// is its own small component rather than a reuse of `Npc`: `Npc`'s non-
// patrol path reads position from React props, which only update at
// whatever cadence the parent re-renders (correct for a consumer-driven
// NPC that changes position occasionally), while a remote player's motion
// must be sampled from the shared `Interpolator` every rendered frame to
// look smooth between the sparse ~150ms transform broadcasts it is built
// from (roadmap 3.1) - the same reason `RemotePlayers.tsx` (3D) does not
// reuse `PlacedCharacter`'s own prop-driven positioning either.
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import type { Mesh } from 'three';

import type { Sprite2D } from '../../content/schema/map';
import { drawSprite, canvasTexture, drawSpriteCells } from '../twod/spriteCanvas';
import { spriteAnimationCells } from '../twod/sprite2d';
import { isSpriteEmpty, facingScaleX, type AnimationClock, stepAnimationClock } from '../twod/npc2d';
import { TILE_SIZE } from '../twod/topDown';
import type { Interpolator, InterpolatedTransform } from './interpolator';

/** Below this speed (tiles/second), a remote player reads as idle rather
 * than walking - same reasoning as `RemotePlayers.tsx`'s own threshold,
 * just in 2D tile units instead of 3D metres. */
const MOVING_SPEED_THRESHOLD = 0.05;

interface RemotePlayer2DProps {
  id: string;
  interpolator: Interpolator<InterpolatedTransform>;
  sprite: Sprite2D;
}

function RemotePlayer2D({ id, interpolator, sprite }: RemotePlayer2DProps) {
  const meshRef = useRef<Mesh>(null);
  const isEmpty = useMemo(() => isSpriteEmpty(sprite), [sprite]);
  const flipbook = useMemo(() => {
    if (isEmpty) return null;
    const canvas = drawSprite(sprite);
    return { canvas, texture: canvasTexture(canvas) };
  }, [isEmpty, sprite]);
  const texture = flipbook?.texture ?? null;
  useEffect(() => () => texture?.dispose(), [texture]);

  const clockRef = useRef<AnimationClock>({ name: 'idle', elapsed: 0 });
  const lastRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const facingRef = useRef(1 as 1 | -1);

  useFrame((_, dt) => {
    const now = Date.now();
    const sample = interpolator.sampleOne(id, now);
    if (!sample) return;
    // 2D top-down convention (see GameRuntime.tsx's own onPlayerTransform
    // wiring): x/z carry tile coordinates as reported by the remote
    // client's own local player (x, 0, y), the same raw continuous
    // position Tilemap2DScene's own `Player` renders from directly - not
    // the +0.5-tile-centred convention a placed `Npc` uses.
    const x = sample.x;
    const y = sample.z;

    const last = lastRef.current;
    const elapsed = last ? (now - last.time) / 1000 : 0;
    const dx = last ? x - last.x : 0;
    const dy = last ? y - last.y : 0;
    const speed = elapsed > 0 ? Math.hypot(dx, dy) / elapsed : 0;
    lastRef.current = { x, y, time: now };
    if (dx !== 0) facingRef.current = facingScaleX(dx > 0 ? 'right' : 'left');

    const targetAnim = speed > MOVING_SPEED_THRESHOLD ? 'walk' : 'idle';
    clockRef.current = stepAnimationClock(clockRef.current, targetAnim, dt);

    if (flipbook) {
      const cells = spriteAnimationCells(sprite, clockRef.current.name, clockRef.current.elapsed);
      drawSpriteCells(flipbook.canvas, sprite.size, cells);
      flipbook.texture.needsUpdate = true;
    }

    const mesh = meshRef.current;
    if (mesh) {
      mesh.position.set(x, -y, 0.1);
      mesh.scale.x = facingRef.current;
    }
  });

  return (
    <mesh ref={meshRef}>
      <planeGeometry args={[TILE_SIZE * 0.8, TILE_SIZE * 0.8]} />
      {texture ? (
        <meshBasicMaterial map={texture} transparent toneMapped={false} />
      ) : (
        <meshBasicMaterial color="#71b4fe" toneMapped={false} />
      )}
    </mesh>
  );
}

export interface RemotePlayers2DProps {
  /** Every currently-connected remote player's id (relay presence, minus
   * the local player) - same contract as `RemotePlayers.tsx`'s own `ids`. */
  ids: readonly string[];
  interpolator: Interpolator<InterpolatedTransform>;
  /** The project's own configured player sprite (`Tilemap2D.playerSprite`) -
   * every remote player renders with this same sprite, the 2D counterpart
   * to `RemotePlayers.tsx`'s "every remote player uses the local player's
   * own default appearance". `undefined` (no sprite authored yet) renders
   * nothing, matching `Player`'s own graceful degrade. */
  sprite: Sprite2D | undefined;
}

/** One sprite per connected remote player (roadmap Phase 4), positioned
 * from the shared `Interpolator`'s own per-frame sampling. */
export function RemotePlayers2D({ ids, interpolator, sprite }: RemotePlayers2DProps) {
  if (ids.length === 0 || !sprite) return null;
  return (
    <>
      {ids.map((id) => (
        <RemotePlayer2D key={id} id={id} interpolator={interpolator} sprite={sprite} />
      ))}
    </>
  );
}
